// client/src/components/fornecedores/Fornecedores.js
import React, { Fragment, useState, useCallback } from 'react';
import {
  useFornecedores,
  useMutateFornecedores
} from '../../hooks/useFornecedores';
import FornecedorForm from './FornecedorForm';
import FornecedorRow from './FornecedorRow';
import FiltroFornecedores from './FiltroFornecedores';
import { PlusCircle, Filter as FilterIcon, Loader2, Building, XCircle, SquarePen, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const Fornecedores = () => {
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [editingFornecedor, setEditingFornecedor] = useState(null);
  const [selectedFornecedor, setSelectedFornecedor] = useState(null);

  const itemsPerPage = 10;

  const {
    fornecedores,
    isLoading: loading,
    count,
    pagination,
    refetch
  } = useFornecedores({
    page: currentPage,
    limit: itemsPerPage,
    ...filters
  });

  const { deleteFornecedor, toggleStatusFornecedor, isPending } = useMutateFornecedores();

  const handleAdicionarClick = useCallback(() => {
    setEditingFornecedor(null);
    setShowForm(true);
    setShowFilters(false);
  }, []);

  const handleEditFornecedor = useCallback((fornecedor) => {
    setEditingFornecedor(fornecedor);
    setShowForm(true);
    setShowFilters(false);
    setSelectedFornecedor(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDeleteFornecedor = useCallback(async (fornecedorId, nome) => {
    if (window.confirm(`Tem certeza que deseja deletar: ${nome}?`)) {
      try {
        await deleteFornecedor.mutateAsync(fornecedorId);
        setSelectedFornecedor(null);
      } catch (error) {
        console.error('Erro ao deletar fornecedor:', error);
      }
    }
  }, [deleteFornecedor]);

  const handleToggleStatus = useCallback(async (fornecedorId) => {
    try {
      await toggleStatusFornecedor.mutateAsync(fornecedorId);
      setSelectedFornecedor(null);
    } catch (error) {
      console.error('Erro ao alternar status:', error);
    }
  }, [toggleStatusFornecedor]);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingFornecedor(null);
  }, []);

  const handleUpdateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setCurrentPage(1);
  }, []);

  const getDisplayId = (id) => id ? id.slice(-6).toUpperCase() : 'N/A';

  const paginate = useCallback((page) => {
    if (page !== currentPage) setCurrentPage(page);
  }, [currentPage]);

  const hasFilters = Object.keys(filters).length > 0;

  return (
    <Fragment>
      <div className="page-management-container">
        <PageHeader
          icon={<Building />}
          title="Gerenciamento de Fornecedores"
          actions={<>
            {!showForm && (
              <>
                <button
                  onClick={() => setShowFilters(p => !p)}
                  className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                >
                  <FilterIcon size={18} className="icon"/>
                  {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                </button>
              </>
            )}
            {showForm && (
              <button onClick={handleCloseForm} className="action-button action-button-secondary">
                <XCircle size={18} className="icon"/> Fechar
              </button>
            )}
          </>}
        />

        {showFilters && !showForm && (
          <div className="content-panel">
            <div className="panel-header">
              <h3 className="panel-title">Filtrar Fornecedores</h3>
              {hasFilters && (
                <button onClick={handleClearFilters} className="action-button action-button-secondary">
                  Limpar Filtros
                </button>
              )}
            </div>
            <FiltroFornecedores
              onUpdateFilters={handleUpdateFilters}
              onClearFilters={handleClearFilters}
              activeFilters={filters}
            />
          </div>
        )}

        {showForm && (
          <div className="content-panel">
            <FornecedorForm
              fornecedor={editingFornecedor}
              onCloseForm={handleCloseForm}
              onSuccess={handleCloseForm}
            />
          </div>
        )}
      </div>

      {!showForm && (
        <div className="table-container">
          <div className="table-wrapper">
            {loading && (!fornecedores?.data || fornecedores.data.length === 0) ? (
              <div className="table-loading-state">
                <Loader2 size={24} className="animate-spin"/>
                <span>Carregando fornecedores...</span>
              </div>
            ) : fornecedores?.data?.length > 0 ? (
              <table className="styled-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Status</th>
                    <th>Nome/Razão Social</th>
                    <th>Documento</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {fornecedores.data.map(fornecedor => (
                    <FornecedorRow
                      key={fornecedor._id}
                      fornecedor={fornecedor}
                      displayId={getDisplayId(fornecedor._id)}
                      onOpenActions={setSelectedFornecedor}
                      loading={isPending}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="table-empty-state">
                <Building size={48} />
                <h3>Nenhum fornecedor encontrado</h3>
                <p>
                  {hasFilters
                    ? 'Nenhum fornecedor corresponde aos filtros aplicados.'
                    : 'Clique em "Adicionar Fornecedor" para cadastrar o primeiro.'}
                </p>
                {hasFilters && (
                  <button onClick={handleClearFilters} className="action-button action-button-secondary">
                    Limpar Filtros
                  </button>
                )}
              </div>
            )}
          </div>
          <Pagination
            totalItems={count || 0}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={paginate}
          />
        </div>
      )}

      <FabButton
        onClick={handleAdicionarClick}
        title="Adicionar Fornecedor"
        visible={!showForm}
        icon={<Building size={26} className="icon"/>}
      />

      <ActionsDrawer
        open={!!selectedFornecedor}
        onClose={() => setSelectedFornecedor(null)}
        title={selectedFornecedor?.nomeFantasia || selectedFornecedor?.razaoSocial || 'Fornecedor'}
        subtitle={selectedFornecedor?.emailPrincipal}
        actions={selectedFornecedor ? [
          {
            key: 'editar',
            label: 'Editar',
            icon: <SquarePen size={20} className="icon" />,
            onClick: () => handleEditFornecedor(selectedFornecedor),
          },
          {
            key: 'status',
            label: selectedFornecedor.ativo ? 'Desativar' : 'Ativar',
            icon: selectedFornecedor.ativo ? <ToggleLeft size={20} className="icon" /> : <ToggleRight size={20} className="icon" />,
            onClick: () => handleToggleStatus(selectedFornecedor._id),
            loading: toggleStatusFornecedor.isPending,
          },
          {
            key: 'excluir',
            label: 'Excluir',
            icon: <Trash2 size={20} className="icon" />,
            onClick: () => handleDeleteFornecedor(selectedFornecedor._id, selectedFornecedor.nomeFantasia || selectedFornecedor.razaoSocial),
            destructive: true,
            loading: deleteFornecedor.isPending,
          },
        ] : []}
      />
    </Fragment>
  );
};

export default Fornecedores;
